import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Layout";
import { Modal, ModalField } from "@/pages/Channels";
import { UserPlus, Trash, Buildings, Users, DownloadSimple, UploadSimple, PencilSimple, Prohibit, Key, Crown, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIMEZONES = ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"];
const RESOLUTIONS = ["720p", "1080p", "1440p", "4K"];
const OUTPUTS = ["HLS", "DASH"];
const ROLE_CLS = {
  owner: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/20",
  admin: "text-amber-500 bg-amber-500/10 border-amber-500/20",
  operator: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
  viewer: "text-neutral-400 bg-neutral-500/10 border-neutral-500/20",
};

export default function Settings() {
  const { user, org, reload } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const [users, setUsers] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: "", logo: null, description: "", default_timezone: "UTC", default_resolution: "1080p", default_output: "HLS", contact_email: "", contact_phone: "" });
  const [modal, setModal] = useState(null); // {type, user}
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "operator" });
  const [editRole, setEditRole] = useState("operator");
  const [newPass, setNewPass] = useState("");
  const importRef = useRef();

  const loadUsers = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => {
    loadUsers();
    const s = org?.settings || {};
    setOrgForm({
      name: org?.name || "", logo: org?.logo || null,
      description: s.description || "", default_timezone: s.default_timezone || "UTC",
      default_resolution: s.default_resolution || "1080p", default_output: s.default_output || "HLS",
      contact_email: s.contact_email || "", contact_phone: s.contact_phone || "",
    });
  }, [org, loadUsers]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setOrgForm({ ...orgForm, logo: r.result });
    r.readAsDataURL(file);
  };

  const saveOrg = async () => {
    try {
      await api.put("/organization", {
        name: orgForm.name, logo: orgForm.logo,
        settings: {
          description: orgForm.description, default_timezone: orgForm.default_timezone,
          default_resolution: orgForm.default_resolution, default_output: orgForm.default_output,
          contact_email: orgForm.contact_email, contact_phone: orgForm.contact_phone,
        },
      });
      toast.success("Organization updated"); reload();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", invite);
      toast.success("User added"); setModal(null);
      setInvite({ name: "", email: "", password: "", role: "operator" }); loadUsers();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveRole = async (e) => {
    e.preventDefault();
    try { await api.put(`/users/${modal.user.id}`, { role: editRole }); toast.success("Role updated"); setModal(null); loadUsers(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try { await api.post(`/users/${modal.user.id}/reset-password`, { password: newPass }); toast.success("Password reset"); setModal(null); setNewPass(""); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggleDisable = async (u) => {
    try { await api.post(`/users/${u.id}/${u.disabled ? "enable" : "disable"}`); toast.success(u.disabled ? "User enabled" : "User disabled"); loadUsers(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const transfer = async () => {
    try { await api.post(`/users/${modal.user.id}/transfer-ownership`); toast.success("Ownership transferred"); setModal(null); loadUsers(); reload(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User removed"); loadUsers(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const exportConfig = async () => {
    try {
      const { data } = await api.get("/system/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ccs-config-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url); toast.success("Configuration exported");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const importConfig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/system/import", json);
      toast.success(`Imported ${data.imported.channels} channels, ${data.imported.assets} assets, ${data.imported.blocks} blocks`);
      reload();
    } catch (err) { toast.error(err?.response ? formatError(err.response?.data?.detail) : "Invalid configuration file"); }
    finally { if (importRef.current) importRef.current.value = ""; }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Organization, users & platform configuration" />
      <div className="p-8 max-w-4xl space-y-6 ccs-enter">
        {/* Organization */}
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-5" data-testid="org-settings">
          <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2"><Buildings size={16} className="text-[#3A86FF]" /> Organization</h3>
          <div className="flex items-start gap-5 mb-5">
            <div>
              <div className="h-20 w-20 rounded-sm border border-[#262626] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                {orgForm.logo ? <img src={orgForm.logo} alt="logo" className="max-h-full max-w-full object-contain" /> : <Buildings size={26} className="text-[#525252]" />}
              </div>
              {canManage && <input data-testid="org-logo" type="file" accept="image/*" onChange={onLogo} className="text-[10px] text-[#A3A3A3] mt-2 w-20" />}
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Labeled label="Name"><input data-testid="org-name-input" value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} disabled={!canManage} className={inputCls} /></Labeled>
              <Labeled label="Description"><input data-testid="org-desc" value={orgForm.description} onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })} disabled={!canManage} className={inputCls} /></Labeled>
              <Labeled label="Default time zone">
                <select data-testid="org-timezone" value={orgForm.default_timezone} onChange={(e) => setOrgForm({ ...orgForm, default_timezone: e.target.value })} disabled={!canManage} className={inputCls}>
                  {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Labeled>
              <Labeled label="Default resolution">
                <select data-testid="org-resolution" value={orgForm.default_resolution} onChange={(e) => setOrgForm({ ...orgForm, default_resolution: e.target.value })} disabled={!canManage} className={inputCls}>
                  {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Labeled>
              <Labeled label="Default output">
                <select data-testid="org-output" value={orgForm.default_output} onChange={(e) => setOrgForm({ ...orgForm, default_output: e.target.value })} disabled={!canManage} className={inputCls}>
                  {OUTPUTS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Labeled>
              <Labeled label="Contact email"><input data-testid="org-contact-email" value={orgForm.contact_email} onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })} disabled={!canManage} className={inputCls} /></Labeled>
              <Labeled label="Contact phone"><input data-testid="org-contact-phone" value={orgForm.contact_phone} onChange={(e) => setOrgForm({ ...orgForm, contact_phone: e.target.value })} disabled={!canManage} className={inputCls} /></Labeled>
            </div>
          </div>
          {canManage && <button data-testid="save-org-btn" onClick={saveOrg} className="bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">Save changes</button>}
        </div>

        {/* Team Members */}
        <div className="bg-[#141414] border border-[#262626] rounded-sm">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
            <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Users size={16} className="text-[#3A86FF]" /> Team Members</h3>
            {canManage && <button data-testid="invite-user-btn" onClick={() => setModal({ type: "invite" })} className="flex items-center gap-2 text-xs text-[#3A86FF] hover:underline"><UserPlus size={14} /> Add User</button>}
          </div>
          {!users ? <Loading /> : (
            <div>
              {users.map((u) => (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between px-5 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1F1F1F] transition-colors duration-150">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-sm bg-[#0A0A0A] border border-[#262626] flex items-center justify-center font-head font-bold text-xs">{u.name?.[0]?.toUpperCase()}</div>
                    <div>
                      <div className="text-sm flex items-center gap-2">{u.name}{u.disabled && <span className="text-[9px] font-mono uppercase tracking-wider text-red-500 border border-red-500/30 rounded-sm px-1.5 py-0.5">Disabled</span>}</div>
                      <div className="font-mono text-[10px] text-[#525252]">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${ROLE_CLS[u.role]}`}>{u.role}</span>
                    {canManage && u.role !== "owner" && u.id !== user.id && (
                      <>
                        <IconBtn testid={`edit-role-${u.id}`} title="Change role" onClick={() => { setEditRole(u.role); setModal({ type: "role", user: u }); }}><PencilSimple size={15} /></IconBtn>
                        <IconBtn testid={`reset-pass-${u.id}`} title="Reset password" onClick={() => { setNewPass(""); setModal({ type: "reset", user: u }); }}><Key size={15} /></IconBtn>
                        <IconBtn testid={`toggle-disable-${u.id}`} title={u.disabled ? "Enable" : "Disable"} onClick={() => toggleDisable(u)}><Prohibit size={15} className={u.disabled ? "text-emerald-500" : ""} /></IconBtn>
                        {isOwner && <IconBtn testid={`transfer-${u.id}`} title="Transfer ownership" onClick={() => setModal({ type: "transfer", user: u })}><Crown size={15} className="hover:text-[#3A86FF]" /></IconBtn>}
                        <IconBtn testid={`delete-user-${u.id}`} title="Remove" onClick={() => removeUser(u.id)} danger><Trash size={15} /></IconBtn>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Configuration Backup */}
        {canManage && (
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-5" data-testid="config-backup">
            <h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-2"><DownloadSimple size={16} className="text-[#3A86FF]" /> Configuration Backup</h3>
            <p className="text-xs text-[#737373] mb-4">Export or import channels, assets, blocks, outputs and settings as JSON. Media files and user passwords are never exported.</p>
            <div className="flex items-center gap-3">
              <button data-testid="export-config-btn" onClick={exportConfig} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#3A86FF] transition-colors duration-150 text-sm px-4 py-2 rounded-sm"><DownloadSimple size={15} /> Export</button>
              {isOwner && (
                <>
                  <button data-testid="import-config-btn" onClick={() => importRef.current?.click()} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#3A86FF] transition-colors duration-150 text-sm px-4 py-2 rounded-sm"><UploadSimple size={15} /> Import</button>
                  <input ref={importRef} data-testid="import-config-file" type="file" accept="application/json" onChange={importConfig} className="hidden" />
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {modal?.type === "invite" && (
        <Modal title="Add Team Member" onClose={() => setModal(null)}>
          <form onSubmit={addUser} className="space-y-4">
            <ModalField label="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} testid="invite-name" required />
            <ModalField label="Email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} testid="invite-email" required />
            <ModalField label="Temporary password" type="text" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} testid="invite-password" required />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Role</label>
              <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className={inputCls + " capitalize"}>
                <option value="admin">Admin</option><option value="operator">Operator</option><option value="viewer">Viewer</option>
              </select>
            </div>
            <button type="submit" data-testid="invite-submit" className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm">Add User</button>
          </form>
        </Modal>
      )}

      {modal?.type === "role" && (
        <Modal title={`Change role — ${modal.user.name}`} onClose={() => setModal(null)}>
          <form onSubmit={saveRole} className="space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Role</label>
              <select data-testid="edit-role-select" value={editRole} onChange={(e) => setEditRole(e.target.value)} className={inputCls + " capitalize"}>
                <option value="admin">Admin</option><option value="operator">Operator</option><option value="viewer">Viewer</option>
              </select>
            </div>
            <button type="submit" data-testid="edit-role-submit" className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm">Save role</button>
          </form>
        </Modal>
      )}

      {modal?.type === "reset" && (
        <Modal title={`Reset password — ${modal.user.name}`} onClose={() => setModal(null)}>
          <form onSubmit={doReset} className="space-y-4">
            <ModalField label="New password" type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)} testid="reset-pass-input" required />
            <button type="submit" data-testid="reset-pass-submit" className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm">Reset password</button>
          </form>
        </Modal>
      )}

      {modal?.type === "transfer" && (
        <Modal title="Transfer Ownership" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-[#A3A3A3]">Transfer Studio Owner to <span className="text-white font-medium">{modal.user.name}</span>? You will be demoted to <span className="text-amber-500">Admin</span>. This cannot be undone by you afterwards.</p>
            <button data-testid="transfer-confirm" onClick={transfer} className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm flex items-center justify-center gap-2"><Crown size={16} /> Confirm transfer</button>
          </div>
        </Modal>
      )}
    </>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none transition-colors duration-150 disabled:opacity-60";

function Labeled({ label, children }) {
  return (<div><label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>{children}</div>);
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`text-[#737373] p-1 transition-colors duration-150 ${danger ? "hover:text-red-500" : "hover:text-white"}`}>
      {children}
    </button>
  );
}
